import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { DashboardWidget } from '../../types/Dashboard';

interface WidgetSettingsProps {
  widget: DashboardWidget | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: Record<string, any>) => void;
}

const WidgetSettings: React.FC<WidgetSettingsProps> = ({ widget, isOpen, onClose, onSave }) => {
  const [title, setTitle] = useState(widget?.title || '');
  const [config, setConfig] = useState<Record<string, any>>(widget?.config || {});
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '180d' | '365d' | 'custom'>(
    widget?.config?.dateRange || '30d'
  );
  const [startDate, setStartDate] = useState(widget?.config?.startDate || '');
  const [endDate, setEndDate] = useState(widget?.config?.endDate || '');

  useEffect(() => {
    if (widget) {
      setTitle(widget.title || '');
      setConfig(widget.config || {});
      setDateRange(widget.config?.dateRange || '30d');
      setStartDate(widget.config?.startDate || '');
      setEndDate(widget.config?.endDate || '');
    }
  }, [widget]);

  if (!isOpen || !widget) return null;

  const isChartWidget = ['line-chart', 'bar-chart', 'pie-chart', 'area-chart'].includes(widget.type);

  const chartTypeOptions: Record<string, { [key: string]: string }> = {
    'line-chart': {
      'receita-vs-alunos': 'Receita vs Alunos',
      'evolucao-aprovacoes': 'Evolução de Aprovações',
      'evolucao-receita': 'Evolução de Receita',
      'evolucao-alunos': 'Evolução de Alunos'
    },
    'bar-chart': {
      'receita-mensal': 'Receita Mensal',
      'mensalidades-pagas-vs-pendentes': 'Mensalidades Pagas vs Pendentes',
      'avaliacoes-por-nivel': 'Avaliações por Nível',
      'avaliacoes-por-professor': 'Avaliações por Professor'
    },
    'pie-chart': {
      'distribuicao-niveis': 'Distribuição por Níveis',
      'distribuicao-idades': 'Distribuição de Idades',
      'distribuicao-notas': 'Distribuição de Notas'
    },
    'area-chart': {
      'evolucao-alunos': 'Evolução de Alunos',
      'progresso-alunos': 'Progresso de Alunos',
      'receita-acumulada': 'Receita Acumulada'
    }
  };

  const handleSave = () => {
    const updatedConfig = {
      ...config,
      title,
      dateRange,
      ...(dateRange === 'custom' && { startDate, endDate })
    };
    onSave(updatedConfig);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Configurar Widget</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título do Widget
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Digite o título do widget"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Widget
              </label>
              <p className="text-sm text-gray-500 capitalize">{widget.type.replace('-', ' ')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tamanho
              </label>
              <p className="text-sm text-gray-500 capitalize">{widget.size}</p>
            </div>

            {/* Campos específicos para widgets de gráfico */}
            {isChartWidget && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Gráfico
                  </label>
                  <select
                    value={config.chartType || ''}
                    onChange={(e) => setConfig({ ...config, chartType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Selecione um tipo</option>
                    {chartTypeOptions[widget.type] && Object.entries(chartTypeOptions[widget.type]).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Período de Dados
                  </label>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-2"
                  >
                    <option value="7d">Últimos 7 dias</option>
                    <option value="30d">Últimos 30 dias</option>
                    <option value="90d">Últimos 90 dias</option>
                    <option value="180d">Últimos 180 dias</option>
                    <option value="365d">Últimos 365 dias</option>
                    <option value="custom">Período personalizado</option>
                  </select>

                  {dateRange === 'custom' && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Data Inicial</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Data Final</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default WidgetSettings;

